import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "antd";
import {
  FileTextOutlined,
  SyncOutlined,
  DatabaseOutlined,
  MailOutlined,
  AlertOutlined,
  TruckOutlined,
  FileProtectOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined
} from "@ant-design/icons";
import "./AdminTools.css";
import { useAuth } from "../AuthProvider";

const adminTools = [
  {
    title: "Custom Commission Contract",
    description:
      "Create contracts with all products, for any distributor, regardless of their current status.",
    route: "/CommissionContractAllProducts",
    icon: <FileTextOutlined />,
    category: "commissions",
  },
  {
    title: "Update Commission Contract",
    description:
      "Check and update existing distributor contracts with missing commission rates.",
    route: "/quickupdateccp",
    icon: <FileTextOutlined />,
    category: "commissions",
  },
  {
    title: "Contract Rights Settings",
    description:
      "Configure the House Distributor used by contract rights workflows.",
    route: "/ContractRightsSettings",
    icon: <FileProtectOutlined />,
    category: "commissions",
  },
  {
    title: "User Permissions",
    description:
      "Manage what AS Users, Distributor Managers, and Account Users can see and do.",
    route: "/UserPermissions",
    icon: <SafetyCertificateOutlined />,
    category: "general",
  },
  {
    title: "Data Change Request 🔗",
    description:
      "Submit a request to change data that is incorrect or cannot be fixed via the app.",
    route: "https://form.asana.com/?k=zQSX_srvRvPvRRI_5kACtw&d=1185407055612481",
    icon: <DatabaseOutlined />,
    category: "data",
    external: true,
  },
  {
    title: "Hubspot to Tissue Vault Sync",
    description:
      "Check and monitor the import sync status from Hubspot into Tissue Vault.",
    route: "/HubspotToTissueVaultSync",
    icon: <SyncOutlined />,
    category: "hubspot",
  },
  {
    title: "Tissue Vault to Hubspot Sync",
    description:
      "Check and monitor the export sync status from Tissue Vault into Hubspot.",
    route: "/TissueVaultToHubspotSync",
    icon: <SyncOutlined />,
    category: "hubspot",
  },
  {
    title: "Email TV Users (coming soon)",
    description:
      "Send an email to all Tissue Vault users, grouped by user types and roles.",
    route: "",
    icon: <MailOutlined />,
    category: "general",
  },
  {
    title: "Create Banner Alert",
    description:
      "Create a banner alert to display important messages to all Tissue Vault users in the top bar.",
    route: "/BannerAlerts",
    icon: <AlertOutlined />,
    category: "general",
  },
  {
    title: "Bulk Edit Account Manufacturer (coming soon)",
    description:
      "Bulk edit the manufacturer relationships associated with multiple accounts at once.",
    route: "",
    icon: <DatabaseOutlined />,
    category: "data",
  },
  {
    title: "Manage Product Categories",
    description:
      "Add, rename, delete, and restore product categories, and move product groups between them.",
    route: "/ProductCategories",
    icon: <AppstoreOutlined />,
    category: "products",
  },
    {
    title: "Shipping Setup",
    description:
      "Configure shipping setup options and defaults used during order management.",
    route: "/ShippingSetup",
    icon: <TruckOutlined />,
    category: "orders",
  },
];

const AdminTools = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    const userData = user;
    if (!userData) return;

    if (userData.Type !== "Admin" || !userData.IsSuperAdmin) {
      navigate("/");
      return;
    }
  }, [navigate, user]);

  const handleToolClick = (tool) => {
    if (!tool.route) return;

    if (tool.external) {
      window.open(tool.route, "_blank", "noopener,noreferrer");
    } else {
      navigate(tool.route);
    }
  };

  const renderTools = (category) =>
    adminTools
      .filter((tool) => tool.category === category)
      .map((tool) => (
        <div
          key={tool.title}
          className="admin-tool-card"
          onClick={() => handleToolClick(tool)}
        >
          <Card hoverable className="admin-tool-card-inner">
            <div className="admin-tool-card-content">
              <div className="admin-tool-icon">{tool.icon}</div>
              <div className="admin-tool-text">
                <div className="text text-bodyText text-bold">
                  {tool.title}
                </div>
                <div className="text text-smallText">
                  {tool.description}
                </div>
              </div>
            </div>
          </Card>
        </div>
      ));

  return (
    <div className="admin-tools-wrapper">
      <div className="text text-h1">⚙️ Admin Tools</div>
      <div className="text text-smallText admin-tools-subtitle">
        Select a tool below to manage different areas of the system.
      </div>

      <div className="spacer-24" />

      {/* General */}
      <div className="admin-tools-row">
        <div className="text text-h2">General</div>
        <div className="text text-smallText admin-tools-subtitle">
          General administrative tools for communication and alerts
        </div>
        <div className="admin-tools-grid">{renderTools("general")}</div>
      </div>

      {/* Orders */}
      <div className="admin-tools-row">
        <div className="text text-h2">Order Management</div>
        <div className="text text-smallText admin-tools-subtitle">
          Tools for managing orders and shipping settings.
        </div>
        <div className="admin-tools-grid">{renderTools("orders")}</div>
      </div>

      {/* Product Management */}
      <div className="admin-tools-row">
        <div className="text text-h2">Product Management</div>
        <div className="text text-smallText admin-tools-subtitle">
          Tools for managing product categories and product groups.
        </div>
        <div className="admin-tools-grid">{renderTools("products")}</div>
      </div>

      {/* Data */}
      <div className="admin-tools-row">
        <div className="text text-h2">Data Management</div>
        <div className="text text-smallText admin-tools-subtitle">
          Requests and data maintenance tools
        </div>
        <div className="admin-tools-grid">{renderTools("data")}</div>
      </div>

      {/* Commissions */}
      <div className="admin-tools-row">
        <div className="text text-h2">Commissions</div>
        <div className="text text-smallText admin-tools-subtitle">
          Tools to help you manage the commission module
        </div>
        <div className="admin-tools-grid">{renderTools("commissions")}</div>
      </div>

      {/* Hubspot Sync */}
      <div className="admin-tools-row">
        <div className="text text-h2">Hubspot Sync</div>
        <div className="text text-smallText admin-tools-subtitle">
          Tools to help you manage the Hubspot-Tissue Vault sync module
        </div>
        <div className="admin-tools-grid">{renderTools("hubspot")}</div>
      </div>
    </div>
  );
};

export default AdminTools;
